// AdvanceRequest.js
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  TextField,
  InputAdornment
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import SearchIcon from '@mui/icons-material/Search';
import CustomCard from '../../components/UIComponents/CustomCard';
import BasicTable from '../../components/Table';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/auth.context';
import { useLoading } from '../../context/loading.context';
import { getReportData, Employeedetails } from '../../service/network';
import StyledAutocomplete from '../../components/UIComponents/StyledAutocomplete';
import StyledTextField from '../../components/UIComponents/StyledTextField';

// Status badge component for advance requests
const StatusBadge = ({ status, approval }) => {
  const statusConfig = {
    'Pending': { color: 'warning', label: 'Pending' },
    'Unpaid': { color: 'warning', label: 'Unpaid' },
    'Approved': { color: 'success', label: 'Approved' },
    'Returned': { color: 'success', label: 'Returned' },
    'Rejected': { color: 'error', label: 'Rejected' },
    'Paid': { color: 'info', label: 'Processed' },
    'Cancelled': { color: 'error', label: 'Cancelled' }
  };

  let config = statusConfig[status] || { color: 'default', label: status };
  if (approval && approval !== "Approved") {
    if (status !== "Draft") {
      config = statusConfig[approval] || { color: 'default', label: approval || status };
    }
  } else {
    config = statusConfig[status] || { color: 'default', label: status }
  }

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{
        fontWeight: 600,
        minWidth: '80px'
      }}
    />
  );
};

const getRows = (data) => {
  if (!data) return [];
  return data.map(item => ({
    ...item,
    status: <StatusBadge status={item.status} approval={item.custom_approval_status} />
  }));
}

// Table configuration
const advanceRequestTableConfig = {
  rows: [
    {
      id: 'id',
      label: 'Request ID',
      flex: 1,
      minWidth: '120px',
      accessor: 'name',
      align: 'left',
      canSort: false
    },
    {
      id: 'employee',
      label: 'Employee',
      flex: 1.5,
      minWidth: '180px',
      accessor: 'employee_name',
      align: 'left',
      canSort: false
    },
    {
      id: 'department',
      label: 'Department',
      flex: 1,
      minWidth: '150px',
      accessor: 'department',
      align: 'left',
      canSort: false
    },
    {
      id: 'purpose',
      label: 'Purpose',
      flex: 1.5,
      minWidth: '200px',
      accessor: 'purpose',
      align: 'left',
      canSort: false
    },
    {
      id: 'amount',
      label: 'Amount',
      flex: 1,
      minWidth: '120px',
      accessor: 'advance_amount',
      align: 'right',
      canSort: false
    },
    {
      id: 'requestDate',
      label: 'Request Date',
      flex: 1,
      minWidth: '120px',
      accessor: 'posting_date',
      align: 'center',
      canSort: false
    },
    {
      id: 'status',
      label: 'Status',
      flex: 1,
      minWidth: '120px',
      accessor: 'status',
      align: 'center',
      canSort: false
    },
    {
      id: 'approvedBy',
      label: 'Approver',
      flex: 1.2,
      minWidth: '150px',
      accessor: 'custom_approver',
      align: 'left',
      canSort: false
    }
  ]
};

export default function AdvanceRequest() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const { selLocation } = useContext(AuthContext);
  const [reqs, setReqs] = useState([]);
  const [filteredReqs, setFilteredReqs] = useState([]);

  const [filters, setFilters] = useState({
    employeeId: '',
    employeeName: '',
    department: '',
    company: selLocation && selLocation[0] ? selLocation[0] : ''
  });

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedEmployeeId = useDebounce(filters.employeeId, 500);
  const debouncedEmployeeName = useDebounce(filters.employeeName, 500);
  const debouncedDepartment = useDebounce(filters.department, 500);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const handleRowClick = (rowData, index) => {
    console.log('Row clicked:', rowData, index);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateRequest = () => {
    navigate('/advance-requests/create');
  };

  const buildApiFilters = () => {
    const apiFilters = [];

    if (filters.employeeId) {
      apiFilters.push(['name', 'like', `%${filters.employeeId}%`]);
    }

    if (filters.employeeName) {
      apiFilters.push(['employee_name', 'like', `%${filters.employeeName}%`]);
    }

    if (filters.department) {
      apiFilters.push(['department', '=', filters.department]);
    }

    if (filters.company) {
      apiFilters.push(['company', '=', filters.company]);
    }

    return apiFilters;
  };

  const applyFilters = () => {
    if (!reqs) return [];

    return reqs.filter(request => {
      // Apply search query filter
      const matchesSearch =
        !debouncedSearchQuery ||
        (request.name && request.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) ||
        (request.employee_name && request.employee_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) ||
        (request.purpose && request.purpose.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) ||
        (request.advance_amount && request.advance_amount.toString().includes(debouncedSearchQuery)) ||
        (request.posting_date && request.posting_date.toLowerCase().includes(debouncedSearchQuery.toLowerCase())) ||
        (request.custom_approver && request.custom_approver.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));

      // Apply other filters
      const matchesEmployeeId =
        !filters.employeeId ||
        (request.employee && request.employee.toLowerCase().includes(filters.employeeId.toLowerCase()));

      const matchesEmployeeName =
        !filters.employeeName ||
        (request.employee_name && request.employee_name.toLowerCase().includes(filters.employeeName.toLowerCase()));

      const matchesDepartment =
        !filters.department ||
        (request.department && request.department === filters.department);

      const matchesCompany =
        !filters.company ||
        (request.company && request.company === filters.company);

      return (
        matchesSearch &&
        matchesEmployeeId &&
        matchesEmployeeName &&
        matchesDepartment &&
        matchesCompany
      );
    });
  };

  const fetchOptions = async (doctype, setterFunction) => {
    try {
      const queryParams = new URLSearchParams({
        doctype,
        fields: JSON.stringify(['name'])
      });

      const response = await Employeedetails(queryParams.toString());

      if (!response?.data?.message?.values) {
        throw new Error(`No data found for ${doctype}`);
      }

      const options = response.data.message.values.map(v => v[0]);
      setterFunction(options);
    } catch (error) {
      console.error(`Failed to fetch ${doctype} options:`, error);
      setterFunction([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      showLoader(true);
      const queryParams = new URLSearchParams({
        doctype: 'Employee',
        fields: JSON.stringify([
          "name as employee_id",
          "employee_name",
          "status",
          "designation",
          "department",
          "gender",
          "ctc",
          "company"
        ]),
        limit_start: '0',
        limit_page_length: '20',
        order_by: 'modified desc'
      });

      const apiFilters = buildApiFilters();
      if (apiFilters.length > 0) {
        queryParams.append('filters', JSON.stringify(apiFilters));
      }

      const response = await Employeedetails(queryParams.toString());

      if (!response?.data?.message) {
        throw new Error('No employee data found');
      }

      const employeeData = response.data.message;

      let transformedData;
      if (Array.isArray(employeeData.values)) {
        transformedData = employeeData.values.map(emp => ({
          employee_id: emp[0] || '',
          employee_name: emp[1] || 'Unknown',
          status: emp[2] || 'Inactive',
          designation: emp[3] || 'N/A',
          department: emp[4] || 'N/A',
          gender: emp[5] || 'N/A',
          ctc: emp[6] || '',
          company: emp[7] || ''
        }));
      } else if (Array.isArray(employeeData)) {
        transformedData = employeeData.map(emp => ({
          employee_id: emp.employee_id || emp.name || '',
          employee_name: emp.employee_name || 'Unknown',
          status: emp.status || 'Inactive',
          designation: emp.designation || 'N/A',
          department: emp.department || 'N/A',
          gender: emp.gender || 'N/A',
          ctc: emp.ctc || '',
          company: emp.company || ''
        }));
      } else {
        throw new Error('Unexpected data format from API');
      }

      setEmployees(transformedData);
    } catch (error) {
      console.error('Fetch error:', error);
      setEmployees([]);
    } finally {
      hideLoader();
    }
  };

  const fetchAdvancesList = async () => {
    try {
      showLoader();

      // Build the base filters
      const baseFilters = [];
      if (selLocation && selLocation[0]) {
        baseFilters.push(["company", "=", selLocation[0]]);
      }

      // Add additional filters if they exist
      const apiFilters = buildApiFilters();
      const combinedFilters = [...baseFilters, ...apiFilters];

      // Fetch advance records
      const advance_pendings_records = await getReportData({
        doctype: 'Employee Advance',
        fields: [
          "name",
          "employee",
          "employee_name",
          "purpose",
          "status",
          "advance_amount",
          "posting_date",
          "paid_amount",
          "return_amount",
          "custom_approver",
          "custom_approval_status",
          "department",
          "company"
        ],
        filters: combinedFilters,
        order_by: 'posting_date desc',
        page_length: 2000
      });

      // Process the response data
      let advances = [];
      if (advance_pendings_records?.data?.message?.values) {
        advances = advance_pendings_records.data.message.values.map(valueArray => {
          const obj = {};
          advance_pendings_records.data.message.keys.forEach((key, index) => {
            // Format specific fields if needed
            if (key === 'posting_date' && valueArray[index]) {
              obj[key] = new Date(valueArray[index]).toLocaleDateString();
            } else if (key === 'advance_amount') {
              obj[key] = `₹ ${parseFloat(valueArray[index] || 0).toFixed(2)}`;
            } else {
              obj[key] = valueArray[index] || '-';
            }
          });
          return obj;
        });
      }

      // Update state
      setReqs(advances);
      setFilteredReqs(advances);

    } catch (err) {
      console.error('Error fetching advance list:', err);
      // Optionally show error to user
      setReqs([]);
      setFilteredReqs([]);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (selLocation && selLocation[0]) {
      setFilters(prev => ({
        ...prev,
        company: selLocation[0]
      }));
      fetchAdvancesList();
    }
  }, [selLocation]);

  useEffect(() => {
    fetchEmployees();
  }, [debouncedEmployeeId, debouncedEmployeeName, debouncedDepartment]);

  useEffect(() => {
    fetchOptions('Department', setDepartmentOptions);
  }, []);

  useEffect(() => {
    const filtered = applyFilters();
    setFilteredReqs(filtered);
  }, [reqs, debouncedSearchQuery, filters]);

  return (
    <Box
      sx={{
        width: '100%',
        p: { xs: 1, sm: 2 },
        boxSizing: 'border-box'
      }}
    >
      <CustomCard
        bgColor="light"
        shadow="lg"
        borderRadius="xxl"
        sx={{
          width: '100%',
          minHeight: 800,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="subTitle" color='primary.main' >
              Advance Requests
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Overview of Advance Requests Details

            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>

            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                px: 3
              }}
              onClick={handleCreateRequest}
            >
              Create Request
            </Button>
          </Box>
        </Box>

        {/* Filter Section */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Grid size={2}>
            <StyledTextField
              label="Employee ID"
              value={filters.employeeId}
              onChange={(e) => handleFilterChange('employeeId', e.target.value)}
              fullWidth
              placeholder="search ID"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid size={2}>
            <StyledTextField
              label="Employee Name"
              value={filters.employeeName}
              onChange={(e) => handleFilterChange('employeeName', e.target.value)}
              fullWidth
              size="small"
              placeholder="search Name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid size={2}>
            <StyledAutocomplete
              label="Department"
              options={departmentOptions}
              value={filters.department}
              onChange={(e, newValue) => handleFilterChange('department', newValue)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid size={2}>
            <StyledTextField
              label="Company"
              value={filters.company}
              onChange={(e) => handleFilterChange('company', e.target.value)}
              fullWidth
              size="small"
              disabled
            />
          </Grid>
        </Box>

        <Box sx={{ flex: 1 }}>
          <BasicTable
            data={{
              rows: advanceRequestTableConfig.rows,
              columns: getRows(filteredReqs)
            }}
            onRowClick={handleRowClick}
            backgroundColor="#f9fafc"
          />
        </Box>
      </CustomCard>
    </Box>
  );
}